<template>
  <modal-fullscreen
    title="Edit Associate"
    :visible="visible"
    :destroy-on-close="true"
    width="600px"
    @cancel="closeModal"
  >
    <rate-modal :visible.sync="visibleRateModal" />
    <actualize-modal :visible.sync="visibleActualizeModal" :show-support="false" @close="closeModalAfterUpdate" />

    <div class="uncompleted-info grid gap-2">
      <h4 class="col-12 p-0 m-0">Rules for Complete Profile</h4>
      <div class="col-12 p-0 flex flex-column" v-for="item in uncompletedRules" :key="item.key">
        <div
          class="flex align-items-center"
          :style="{
            color: getMissedRule(item.key) ? '#10b981' : '#ef4444',
            gap: '8px'
          }"
        >
          {{ item.title }}
          <custom-icon :type="getMissedRule(item.key) ? 'check-circle' : 'close-circle'" />
        </div>
        <p v-if="!getMissedRule(item.key)" :style="{ color: '#999999' }">
          {{ getMissedText(item.key) }}
        </p>
      </div>
    </div>

    <a-form-model
      layout="horizontal"
      ref="form"
      :model="form"
      :rules="rules"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <div class="flex flex-column">
        <div class="col flex flex-column gap-2">
          <h3>Personal</h3>
          <a-form-model-item
            :colon="false"
            label-align="left"
            label="First Name"
            prop="associate.first_name"
            :validate-status="invalidChar.first_name ? 'error' : ''"
            :help="getHelpText('first_name')"
          >
            <a-input
              v-model="form.associate.first_name"
              placeholder="First Name"
              autocomplete="off"
              @input="handleNameInput('first_name', $event)"
            />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Last Name"
            prop="associate.last_name"
            :validate-status="invalidChar.last_name ? 'error' : ''"
            :help="getHelpText('last_name')"
          >
            <a-input
              v-model="form.associate.last_name"
              placeholder="Last Name"
              autocomplete="off"
              @input="handleNameInput('last_name', $event)"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Email" prop="associate.email">
            <a-input v-model="form.associate.email" placeholder="Email" autocomplete="off" />
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Phone" prop="associate.phone">
            <phone-input
              @input="
                value => {
                  $refs.form.validateField('associate.phone')
                  form.associate.phone = value
                }
              "
              :value="form.associate.phone"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Birth Date" prop="associate.birth_date">
            <a-date-picker
              v-model="form.associate.birth_date"
              placeholder="Birth Date"
              class="w-full"
              :allowClear="true"
              :format="visualDateFormat"
            />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Address Line 1"
            prop="associate.address.address_line1"
          >
            <a-input v-model="form.associate.address.address_line1" placeholder="Enter Address" />
          </a-form-model-item>
          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Address Line 2"
            prop="associate.address.address_line2"
          >
            <a-input v-model="form.associate.address.address_line2" placeholder="Enter Address" />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="City" prop="associate.address.city">
            <a-input v-model="form.associate.address.city" placeholder="Enter City" />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Country" prop="associate.address.country">
            <custom-select
              :multiple="false"
              :value.sync="countryProxy"
              placeholder="Enter Country"
              :options="countriesList"
              name="name"
              optionKey="code"
            />
          </a-form-model-item>

          <a-form-model-item
            v-if="form.associate.address.country === 'US' || form.associate.address.country === 'us'"
            :colon="false"
            label-align="left"
            label="State"
            prop="associate.address.state"
          >
            <custom-select
              :multiple="false"
              :value.sync="stateProxy"
              placeholder="Enter State"
              :options="statesList"
              name="name"
              optionKey="code"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Zipcode" prop="associate.address.zipcode">
            <a-input v-model="form.associate.address.zipcode" placeholder="Enter Zipcode" />
          </a-form-model-item>
        </div>
        <div class="col flex flex-column gap-2">
          <h3>Job</h3>

          <a-form-model-item :colon="false" label-align="left" label="Position" prop="associate.role.id">
            <custom-select
              :multiple="false"
              :allow-clear="false"
              :value.sync="form.associate.role.id"
              placeholder="Position"
              :options="roles"
              @select="openSuggestionModal"
              name="name"
              optionKey="id"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Schedule Type" prop="associate.schedule_type">
            <a-select v-model="form.associate.schedule_type" placeholder="Enter schedule type">
              <a-select-option v-for="(value, key) in scheduleTypes" :key="key" :value="key">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Worker ID" prop="updated_associate_number">
            <a-input v-model="form.updated_associate_number" placeholder="Worker ID" autocomplete="off" />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="PBJ ID" prop="associate.pbj_id">
            <a-input v-model="form.associate.pbj_id" placeholder="PBJ ID" autocomplete="off" />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Payroll ID" prop="associate.payroll_id">
            <a-input v-model="form.associate.payroll_id" placeholder="Payroll ID" autocomplete="off" />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            :label="getTaxIdentifierLabel(form.associate.is_ssn)"
            prop="associate.social_security_number"
          >
            <tax-identifier-input
              v-model="form.associate.social_security_number"
              :is-ssn.sync="form.associate.is_ssn"
              @type-change="revalidateTaxIdentifier('associate.social_security_number')"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Hire Date" prop="associate.hire_date">
            <a-date-picker
              v-model="form.associate.hire_date"
              placeholder="Hire Date"
              class="w-full"
              :allowClear="true"
              :format="visualDateFormat"
            />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            :label="terminatedDateLabel"
            prop="associate.terminated_date"
          >
            <a-date-picker
              v-model="form.associate.terminated_date"
              :placeholder="terminatedDateLabel"
              class="w-full"
              :allowClear="true"
              :format="visualDateFormat"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Seniority Date" prop="associate.seniority_date">
            <a-date-picker
              v-model="form.associate.seniority_date"
              :allowClear="true"
              placeholder="Seniority Date"
              class="w-full"
              :format="visualDateFormat"
            />
          </a-form-model-item>
        </div>
        <div class="col flex flex-column gap-2">
          <div class="flex justify-content-between">
            <h3>Employment</h3>
            <a-button
              v-if="
                hasUncontrolledPermission('manage_associates', {
                  id: associate.id
                })
              "
              @click="visibleRateModal = true"
              class="flex gap-2 align-items-center"
            >
              <a-icon width="16px" height="16px" icon="material-symbols:edit-square-outline" />
              Change Rate</a-button
            >
          </div>
          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Employment Type"
            prop="associate.payment_info.type"
          >
            <a-select
              :disabled="!!associate.staff_associate"
              v-model="form.associate.payment_info.type"
              @select="openSuggestionModal"
              placeholder="Enter payment type"
            >
              <a-select-option v-for="(value, key) in paymentTypes" :key="key" :value="key">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
          <template v-if="form.associate.payment_info.type !== 'Volunteer'">
            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Employment Status"
              prop="associate.payment_info.status"
            >
              <a-select
                :disabled="!!associate.staff_associate"
                v-model="form.associate.payment_info.status"
                placeholder="Enter payment status"
              >
                <a-select-option v-for="(value, key) in paymentStatuses" :key="key" :value="key">
                  {{ value }}
                </a-select-option>
              </a-select>
            </a-form-model-item></template
          >

          <a-form-model-item
            v-if="form.associate.payment_info.type === 'Contractor'"
            :colon="false"
            label-align="left"
            label="Legal Entity"
            prop="associate.payment_info.legal_entity"
            :extra="
              associate.staff_associate
                ? 'Changing the Legal Entity is UNAVAILABLE because their profile is CONNECTED with the Staff Company. You need to disconnect the profile before changing the Legal Entity, or remove the person from the members of the Legal Entity!'
                : ''
            "
          >
            <custom-select
              :multiple="false"
              :disabled="!!associate.staff_associate"
              :allow-clear="false"
              :value.sync="legalEntityProxy"
              placeholder="Legal Entity"
              :options="agencies"
              name="name"
              optionKey="id"
              @select="openSuggestionModal"
            >
              <template #option="{ option }"> [{{ option.id }}] - {{ option.name }}</template>
            </custom-select>

            <a-button v-if="canCreateLegalEntity" @click="handleOpenLegalEntity" type="primary" class="w-full"
              >Create New</a-button
            >
          </a-form-model-item>

          <template v-if="canManageExternalStaffLink">
            <p v-if="associate.staff_associate">
              Stop transferring profile data between companies and unblock changes -
              <a-button
                @click.stop="handleExternalLink('unlink_from_staff_associate')"
                class="external-link-button"
                type="link"
                >Disconnect</a-button
              >
            </p>
            <p v-else>
              Start transferring profile data between companies -
              <a-button
                @click.stop="handleExternalLink('link_to_staff_associate')"
                class="external-link-button"
                type="link"
                >Connect</a-button
              >
            </p></template
          >
        </div>
      </div>
    </a-form-model>
    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Close</a-button>
        <div class="flex justify-content-end">
          <a-button :loading="loading" @click="handleSubmit" icon="save" type="primary" ghost>Save</a-button>
        </div>
      </div>
    </template>
  </modal-fullscreen>
</template>

<script>
import { Button, DatePicker, Descriptions, FormModel, Icon, Input, Modal, notification, Select } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import constData from '@/helpers/const-data'
import moment from 'moment-timezone'
import CustomSelect from '@/components/ui/custom-select.vue'
import { deepCopy } from '@/utils/deep-copy'
import profileActualizeShiftVue from './profile-actualize-shift.vue'
import PhoneInput from '@/components/ui/phone-input.vue'
import RateModal from './rate/rate-modal.vue'
import permissionMixin from '@/mixins/permissionMixin'
import TaxIdentifierInput from '@/components/ui/tax-identifier-input.vue'
import { getTaxIdentifierLabel, normalizeTaxIdentifier } from '@/utils/tax-identifier'
import {
  CUSTOM_WEEKLY_WORKING_HOURS,
  WEEKLY_WORKING_HOURS_OPTIONS,
  DAILY_WORKING_HOURS_OPTIONS,
  getDailyWorkingHoursOptions,
  getWeeklyWorkingHoursSelectValue,
  isWorkingHoursPaymentType
} from '@/modules/profile/utils/working-hours-limits'
import {
  createRequiredFieldRule,
  createRequiredTaxIdentifierRule,
  isUsCountry
} from '@/modules/profile/utils/associate-profile-completion'
import { getSeparationDateLabel } from '@/modules/profile/utils/separation'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: modalActions } = createNamespacedHelpers('modals')
const { mapActions: legalEntityActions } = createNamespacedHelpers('legalEntity')

const scheduleTypes = Object.freeze({
  fixed: 'Strict',
  self_schedule: 'Flexible',
  free: 'Free'
})

const paymentTypes = Object.freeze({
  Employee: 'Employee',
  Contractor: 'Contractor',
  Volunteer: 'Volunteer'
})

const paymentStatuses = Object.freeze({
  on_call: 'On Call / Per Diem',
  full_time: 'Full Time',
  part_time: 'Part Time'
})

const initialForm = {
  updated_associate_number: undefined,
  associate: {
    first_name: undefined,
    last_name: undefined,
    email: undefined,
    phone: undefined,
    birth_date: undefined,
    address: {
      address_line1: undefined,
      address_line2: undefined,
      city: undefined,
      state: undefined,
      zipcode: undefined,
      country: undefined
    },
    role: {
      id: undefined
    },
    payment_info: {
      type: undefined,
      status: undefined,
      legal_entity: undefined
    },
    social_security_number: undefined,
    is_ssn: true,
    schedule_type: undefined,
    associate_number: undefined,
    allow_instant_payment: false,
    automatic_completing_actions: false,
    max_working_hours_per_day: 0,
    max_working_hours_per_week: 0,
    hire_date: undefined,
    terminated_date: undefined,
    seniority_date: undefined,
    is_suspected: false,
    lunch_waiver: false,
    payment_auto_approve: false,
    available_auth_types: [],
    permissions: [],
    pbj_id: null,
    payroll_id: null
  }
}

const initialRules = {
  associate: {
    first_name: [
      {
        required: true,
        message: 'First name can not be empty',
        trigger: 'change'
      }
    ],
    last_name: [
      {
        required: true,
        message: 'Last name can not be empty',
        trigger: 'change'
      }
    ],
    email: [
      {
        required: true,
        message: 'Email can not be empty',
        trigger: 'change',
        type: 'email'
      }
    ],
    phone: [
      {
        required: true,
        message: 'Phone can not be empty',
        trigger: 'change'
      },
      {
        transform: value => (value ? value.replace(/\s+/g, '') : value),
        pattern: /^\+\d{7,18}$/,
        message: 'Invalid phone number. It should start with a + and contain 10 to 15 digits.'
      }
    ],
    birth_date: [createRequiredFieldRule('Please enter birth date.')],
    address: {
      address_line1: [createRequiredFieldRule('Please enter address line 1.')],
      city: [createRequiredFieldRule('Please enter city.')],
      country: [createRequiredFieldRule('Please enter country.')],
      zipcode: [
        {
          min: 5,
          message: 'The zipcode must be more than 4 characters.',
          trigger: 'change'
        }
      ]
    },
    social_security_number: [],
    pbj_id: [
      {
        validator: (_, value) => {
          if (!value || /^\d+$/.test(value)) {
            return Promise.resolve()
          }
          return Promise.reject(new Error('Only digits are allowed.'))
        },
        trigger: 'change'
      }
    ],
    payroll_id: [
      {
        validator: (_, value) => {
          if (!value || /^\d+$/.test(value)) {
            return Promise.resolve()
          }
          return Promise.reject(new Error('Only digits are allowed.'))
        },
        trigger: 'change'
      }
    ],
    role: {
      id: [
        {
          required: true,
          message: 'Position can not be empty',
          trigger: 'change'
        }
      ]
    },
    schedule_type: [
      {
        required: true,
        message: 'Schedule type can not be empty',
        trigger: 'change'
      }
    ],
    payment_info: {
      type: [
        {
          required: true,
          message: 'Employment type can not be empty',
          trigger: 'change'
        }
      ],
      status: [
        {
          required: true,
          message: 'Employment status can not be empty',
          trigger: 'change'
        }
      ],
      legal_entity: [
        {
          required: true,
          message: 'Legal Entity can not be empty',
          trigger: 'change'
        }
      ]
    }
  }
}

export default {
  components: {
    'a-modal': Modal,
    'a-button': Button,
    'a-input': Input,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-form-model-item': FormModel.Item,
    'a-form-model': FormModel,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-date-picker': DatePicker,
    'custom-icon': Icon,
    'rate-modal': RateModal,
    'custom-select': CustomSelect,
    'actualize-modal': profileActualizeShiftVue,
    'phone-input': PhoneInput,
    'tax-identifier-input': TaxIdentifierInput
  },
  props: {
    visible: {
      type: Boolean,
      default: true
    }
  },
  emits: ['clone', 'saved'],
  mixins: [permissionMixin],
  data() {
    return {
      form: {
        ...initialForm
      },
      rules: {
        ...initialRules,
        associate: {
          ...initialRules.associate,
          address: {
            ...initialRules.associate.address
          },
          first_name: [
            {
              required: true,
              message: 'Please enter your first name',
              trigger: 'blur'
            },
            { validator: this.associateNameValidator, trigger: 'blur' }
          ],
          last_name: [
            {
              required: true,
              message: 'Please enter your last name',
              trigger: 'blur'
            },
            { validator: this.associateNameValidator, trigger: 'blur' }
          ],
          social_security_number: [createRequiredTaxIdentifierRule(() => this.form.associate.is_ssn)]
        },
        updated_associate_number: [
          {
            required: true,
            message: 'Associate number can not be empty',
            trigger: 'change'
          },
          {
            validator: (_, value) => {
              if (!value || /^\d+$/.test(value)) {
                return Promise.resolve()
              }
              return Promise.reject(new Error('Only digits are allowed.'))
            },
            trigger: 'change'
          },
          {
            len: 6,
            message: 'The associate number must be 6 characters.',
            trigger: 'change'
          },
          {
            validator: this.associateNumberValidator,
            trigger: 'change'
          }
        ]
      },

      paymentStatuses: paymentStatuses,
      paymentTypes: paymentTypes,
      scheduleTypes: scheduleTypes,
      roles: this.$store.state.applicationState.projectRoles,
      weeklyWorkingHoursOptions: WEEKLY_WORKING_HOURS_OPTIONS,
      customWeeklyWorkingHours: CUSTOM_WEEKLY_WORKING_HOURS,
      isCustomWeeklyWorkingHours: false,

      isInitializing: true,
      loading: false,
      initialFormSnapshot: null,

      countriesList: constData.countries,
      statesList: constData.usStates,

      visibleActualizeModal: false,
      visibleRateModal: false,

      baseHelpText: 'Only letters, spaces, hyphens (-), and slashes (/) allowed.',
      invalidChar: {
        first_name: null,
        last_name: null
      }
    }
  },
  watch: {
    'form.associate.address.country': {
      handler(newVal) {
        const isUs = newVal && newVal.toUpperCase() === 'US'

        if (!isUs) this.form.associate.address.state = undefined

        this.syncUsAddressRules(isUs)

        this.$nextTick(() => {
          if (!this.$refs.form) {
            return
          }

          if (isUs) {
            this.$refs.form.validateField('associate.address.state')
            this.$refs.form.validateField('associate.address.zipcode')
            return
          }

          this.$refs.form.clearValidate(['associate.address.state', 'associate.address.zipcode'])
        })
      }
    },
    'form.associate.payment_info.type': {
      handler(newVal) {
        if (this.isInitializing) return

        Object.assign(this.form.associate.payment_info, {
          legal_entity: undefined,
          ...this.getPaymentInfoByType(newVal)
        })

        this.normalizeWorkingHoursLimits()
      },
      immediate: false
    },
    visible: {
      handler(newVal) {
        if (newVal) {
          this.isCustomWeeklyWorkingHours = false
          this.form = {
            associate: deepCopy(this.associate),
            updated_associate_number: this.associate.associate_number
          }
          this.$set(this.form.associate, 'is_ssn', this.form.associate.is_ssn !== false)

          const dateFields = ['hire_date', 'birth_date', 'resigned_date', 'seniority_date', 'terminated_date']

          dateFields.forEach(item => {
            if (this.form.associate[item]) {
              this.form.associate[item] = moment(this.form.associate[item]).utc()
            }
          })

          this.form.associate.address = this.associate.address
            ? deepCopy(this.associate.address)
            : {
                ...initialForm.associate.address
              }

          this.syncUsAddressRules()

          this.normalizeWorkingHoursLimits()

          this.$nextTick(() => {
            this.isInitializing = false
            this.initialFormSnapshot = this.getFormSnapshot()
          })
        }

        if (!newVal) {
          this.isInitializing = true
          this.isCustomWeeklyWorkingHours = false
          this.initialFormSnapshot = null
          this.form = { ...initialForm }
          this.syncUsAddressRules(false)
        }
      },
      immediate: true
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    hasUnsavedChanges() {
      return this.initialFormSnapshot !== null && this.initialFormSnapshot !== this.getFormSnapshot()
    },

    isStaff() {
      return this.$store.state.applicationState.currentProject.staffing
    },

    isVisibleWorkingHoursLimits() {
      return isWorkingHoursPaymentType(this.form.associate.payment_info.type)
    },

    dailyWorkingHoursOptions() {
      return getDailyWorkingHoursOptions()
    },

    isDailyWorkingHoursLimitEnabled() {
      return Number(this.form.associate.max_working_hours_per_day) > 0
    },

    dailyWorkingHoursLimitValue: {
      get() {
        return this.form.associate.max_working_hours_per_day || DAILY_WORKING_HOURS_OPTIONS[0].value
      },
      set(value) {
        this.form.associate.max_working_hours_per_day = value
      }
    },

    weeklyWorkingHoursSelectValue: {
      get() {
        if (this.isCustomWeeklyWorkingHours) {
          return CUSTOM_WEEKLY_WORKING_HOURS
        }

        return getWeeklyWorkingHoursSelectValue(this.form.associate.max_working_hours_per_week)
      },
      set(value) {
        if (value === CUSTOM_WEEKLY_WORKING_HOURS) {
          this.isCustomWeeklyWorkingHours = true

          return
        }

        this.isCustomWeeklyWorkingHours = false
        this.form.associate.max_working_hours_per_week = value === undefined ? 0 : value
      }
    },

    agencies() {
      return this.$store.state.applicationState.projectLegalEntities
    },

    uncompletedRules() {
      const rules = [
        { title: 'Hire Date', key: 'associate.hire_date' },
        { title: 'Seniority Date', key: 'associate.seniority_date' },
        { title: 'Position', key: 'associate.role.id' },
        {
          title: 'Employment Type',
          key: 'associate.payment_info.type'
        },
        {
          title: 'Hourly Rate',
          key: 'associate.payment_info.hourly_rate'
        }
      ]

      if (this.form.associate.payment_info.method === 'salary') {
        rules.push({
          title: 'Salary',
          key: 'associate.payment_info.salary'
        })
      }

      if (this.form.associate.payment_info.type !== 'Volunteer') {
        rules.push({
          title: 'Employment Status',
          key: 'associate.payment_info.status'
        })
      }

      if (this.form.associate.payment_info.type === 'Contractor') {
        rules.push({
          title: 'Legal Entity',
          key: 'associate.payment_info.legal_entity'
        })
      }

      return rules
    },

    stateProxy: {
      get() {
        return this.form.associate.address.state ? this.form.associate.address.state.toUpperCase() : undefined
      },
      set(value) {
        this.form.associate.address.state = value
      }
    },

    countryProxy: {
      get() {
        return this.form.associate.address.country ? this.form.associate.address.country.toUpperCase() : undefined
      },
      set(value) {
        this.form.associate.address.country = value
      }
    },

    legalEntityProxy: {
      get() {
        return this.form.associate.payment_info.legal_entity
          ? this.form.associate.payment_info.legal_entity.id
          : undefined
      },
      set(value) {
        const foundedLegalEntity = this.agencies.find(item => item.id === value)

        this.form.associate.payment_info.legal_entity = JSON.parse(JSON.stringify(foundedLegalEntity))
      }
    },

    canCreateLegalEntity() {
      return !this.associate.staff_associate && !this.form.associate.payment_info.legal_entity
    },

    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    },

    terminatedDateLabel() {
      return getSeparationDateLabel(this.form.associate.termination?.type)
    },

    canManageExternalStaffLink() {
      return this.associate.staff_associate || this.associate.payment_info?.legal_entity?.staffing_status === 'linked'
    },

    isUpdateAssociateNumber() {
      return this.form.updated_associate_number.trim() === this.form.associate.associate_number
    }
  },
  methods: {
    ...modalActions(['openModal']),
    ...profileActions(['checkAvailableAssociateNumber', 'updateAssociate', 'setAssociate']),
    ...legalEntityActions(['getLegalEntity']),

    getTaxIdentifierLabel,

    getUsStateRules(isUs) {
      if (!isUs) {
        return []
      }

      return [
        {
          required: true,
          message: 'Please enter state.',
          trigger: 'change'
        }
      ]
    },

    getZipcodeRules(isUs) {
      const rules = []

      if (isUs) {
        rules.push({
          required: true,
          message: 'Please enter ZIP code.',
          trigger: 'change'
        })
      }

      return [...rules, ...initialRules.associate.address.zipcode]
    },

    syncUsAddressRules(isUs = isUsCountry(this.form.associate.address.country)) {
      this.$set(this.rules.associate.address, 'state', this.getUsStateRules(isUs))
      this.$set(this.rules.associate.address, 'zipcode', this.getZipcodeRules(isUs))
    },

    revalidateTaxIdentifier(field) {
      this.$nextTick(() => {
        this.$refs.form.validateField(field)
      })
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    getMissedRule(rule) {
      const keys = rule.split('.')

      let value = this.form

      for (const key of keys) {
        if (value && key in value) {
          value = value[key]
        } else {
          value = null
          break
        }
      }

      const isValid = value !== null && value !== undefined && value !== '' && value !== 0

      if (rule === 'associate.role.id') {
        const workerRole = this.roles.find(item => item.id === this.form.associate.role.id)

        return isValid && workerRole.name !== 'Worker'
      }

      if (rule === 'associate.payment_info.type') {
        return isValid && this.form.associate.payment_info.type !== 'Volunteer'
      }

      return isValid
    },

    getMissedText(rule) {
      const text = 'Field must be filled'

      if (rule === 'associate.role.id') {
        return `${text} and not equal to Worker`
      }

      if (rule === 'associate.payment_info.type') {
        return `${text} and not equal to  Volunteer`
      }

      return text
    },

    getPaymentInfoByType(type) {
      const paymentConfig = {
        Employee: { status: 'full_time' },
        Volunteer: { status: 'on_call' },
        Contractor: { status: 'on_call' }
      }

      return paymentConfig[type] || {}
    },

    handleSwitchChange(value, name) {
      this.form.associate[name] = value
    },

    handleDailyWorkingHoursLimitChange(value) {
      this.form.associate.max_working_hours_per_day = value ? this.getDailyWorkingHoursLimitFallback() : 0
    },

    getDailyWorkingHoursLimitFallback() {
      const currentValue = this.form.associate.max_working_hours_per_day
      const hasDailyOption = this.dailyWorkingHoursOptions.some(option => option.value === currentValue)

      return hasDailyOption ? currentValue : DAILY_WORKING_HOURS_OPTIONS[0].value
    },

    normalizeWorkingHoursLimits() {
      if (!this.isVisibleWorkingHoursLimits) {
        this.form.associate.max_working_hours_per_day = 0
        this.form.associate.max_working_hours_per_week = 0
        this.isCustomWeeklyWorkingHours = false

        return
      }

      const hasDailyOption = this.dailyWorkingHoursOptions.some(
        option => option.value === this.form.associate.max_working_hours_per_day
      )

      if (this.form.associate.max_working_hours_per_day && !hasDailyOption) {
        this.form.associate.max_working_hours_per_day = 0
      }
    },

    handleExternalLink(type) {
      this.$emit('externalLink', type)
    },

    getFormSnapshot() {
      return JSON.stringify(this.form)
    },

    closeModal() {
      if (this.hasUnsavedChanges) {
        Modal.confirm({
          title: 'Discard profile changes?',
          content: 'Unsaved profile changes will be lost.',
          okText: 'Discard Changes',
          okType: 'danger',
          cancelText: 'Continue Editing',
          onOk: this.closeModalImmediately,
          onCancel() {}
        })
        return
      }

      this.closeModalImmediately()
    },

    closeModalImmediately() {
      this.$emit('update:visible', false)
    },

    closeModalAfterUpdate() {
      this.$emit('saved', this.form.updated_associate_number)
      this.closeModalImmediately()

      this.$emit('edit', this.form.updated_associate_number)
    },

    openSuggestionModal() {
      if (this.isStaff) {
        return
      }

      Modal.confirm({
        title: 'You can create a new profile',
        content:
          "Instead of changing the current profile, you can create a copy of the associate's profile. This allows you to work with a new associate profile while keeping a previous one unchanged.",
        okText: 'Create New Profile',
        okType: 'primary',
        cancelText: 'Update Current Profile',
        onOk: () => {
          this.closeModalImmediately()
          this.$emit('clone')
        },
        onCancel: () => {}
      })
    },

    handleOpenLegalEntity() {
      if (!this.canCreateLegalEntity) return

      this.openModal({
        type: 'legalEntity',
        data: {
          visible: true,
          initialValues: {
            type: 'independent_contractor',
            address: this.form.associate.address,
            name: `${this.form.associate.first_name} ${this.form.associate.last_name}`,
            tax_id: (this.form.associate.social_security_number ?? '').replace(/\D/g, ''),
            admin_ids: [this.form.associate.id]
          },
          add: async legal_entity => {
            this.form.associate.payment_info.legal_entity = deepCopy(legal_entity)
          },
          showCreateAssociate: false
        }
      })
    },

    async openActualizeShift() {
      const shouldClose = await new Promise(resolve => {
        Modal.confirm({
          title: 'Review unpaid shift rates',
          content: 'Choose a rate and period, then review the changes before applying them to unpaid shifts.',
          okText: 'Review rates',
          cancelText: 'Update only profile',
          onOk: () => {
            this.visibleActualizeModal = true
            resolve(false)
          },
          onCancel() {
            resolve(true)
          }
        })
      })

      return shouldClose
    },

    async associateNumberValidator(rule, value, callback) {
      try {
        if (this.isUpdateAssociateNumber) {
          return
        }

        const result = await this.checkAvailableAssociateNumber({
          associate_number: value
        })

        if (result.owner) {
          callback(
            new Error(
              `This associate number is already occupied by another user in this company. You can use available associate number: ${result.available_number} `
            )
          )
        }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },
    associateNameValidator(rule, value, callback) {
      if (!value) {
        return callback()
      }

      const allowedPattern = /^[a-zA-Z\s\-\/]+$/
      if (!allowedPattern.test(value)) {
        return callback(new Error('Only letters, spaces, hyphens (-), and forward slashes (/) are allowed.'))
      }

      if (value.trim() === '') {
        return callback(new Error('Name cannot be empty.'))
      }

      callback()
    },
    handleNameInput(field, e) {
      const rawValue = e.target.value

      const invalidMatch = rawValue.match(/[^a-zA-Z\s\-\/]/)
      const invalidSymbol = invalidMatch ? invalidMatch[0] : null

      this.invalidChar[field] = invalidSymbol

      const cleaned = rawValue.replace(/[^a-zA-Z\s\-\/]/g, '')

      const formatted = cleaned
        .toLowerCase()
        .split(' ')
        .map(word =>
          word
            .split(/([\-\/])/)
            .map(part => {
              if (part === '-' || part === '/') return part
              return part.charAt(0).toUpperCase() + part.slice(1)
            })
            .join('')
        )
        .join(' ')

      this.form.associate[field] = formatted

      if (invalidSymbol) {
        setTimeout(() => {
          if (this.invalidChar[field] === invalidSymbol) {
            this.invalidChar[field] = null
          }
        }, 2000)
      }
    },

    getHelpText(field) {
      if (this.invalidChar[field]) {
        const char = this.invalidChar[field]
        const displayChar = char === ' ' ? 'space' : char === '\t' ? 'tab' : `"${char}"`
        return `Invalid character ${displayChar}. ${this.baseHelpText}`
      }
      return this.baseHelpText
    },

    async submitActions() {
      this.form.associate.phone = this.form.associate.phone.replace(/\s+/g, '')
      this.form.associate.is_ssn = this.form.associate.is_ssn !== false
      this.form.associate.social_security_number =
        normalizeTaxIdentifier(this.form.associate.social_security_number) || null
      this.form.associate.email = this.form.associate.email.toLowerCase()
      this.form.associate.payment_info.legal_entity = this.form.associate.payment_info.legal_entity
        ? await this.getLegalEntity({
            legal_entity_id: this.form.associate.payment_info.legal_entity.id
          })
        : undefined

      const payload = {
        associate: JSON.parse(JSON.stringify(this.form.associate)),
        updated_associate_number: this.isUpdateAssociateNumber ? null : this.form.updated_associate_number
      }

      const data = await this.updateAssociate(payload)

      this.showNotification('success', 'Info', `Associate has been edited successfully`)

      let isModalClose = true

      if (this.form.associate.role.id !== this.associate.role.id) {
        isModalClose = await this.openActualizeShift()
      }

      this.setAssociate({ ...this.associate, ...data })

      if (isModalClose) {
        this.closeModalAfterUpdate()
      }
    },

    handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        try {
          this.loading = true

          await this.submitActions()

          if (this.$store.state.applicationState.associate.id === this.associate.id) {
            await this.$store.dispatch('loadApplicationState')
          }
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
::v-deep .ant-modal-body {
  max-height: 720px;
  overflow: auto;
}

.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.ant-input-number {
  width: 100%;
}

.ant-calendar-picker {
  margin: 0;
  min-width: unset !important;
}

.external-link-button {
  height: auto;
  padding: 0;
  line-height: inherit;
  vertical-align: baseline;
}

.working-hours-limit-control {
  width: 180px;
}

.uncompleted-info {
  position: absolute;
  right: -270px;
  background-color: var(--bg-component);
  width: 250px;
  height: fit-content;
  top: 6px;
  border-radius: 12px;
  padding: 16px 24px;
}

.modal-fullscreen-wrap--expanded .uncompleted-info {
  position: static;
  padding: 0;
  width: 100%;
  margin: 0;
  padding-bottom: 24px;
}

::v-deep .modal-fullscreen-wrap--expanded .ant-modal-body {
  max-height: none;
}

@media screen and (max-width: 1200px) {
  .uncompleted-info {
    right: -200px;
    width: 200px;
    padding: 12px 16px;
  }
}

@media screen and (max-width: 1024px) {
  .uncompleted-info {
    position: static;
    padding: 0;
    width: 100%;
    margin: 0;
    padding-bottom: 24px;
  }

  .uncompleted-info h4 {
    font-size: 18px;
    margin-bottom: 14px !important;
  }
}
</style>
